import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
  ColumnDef,
  OnChangeFn,
  flexRender,
  useReactTable,
  getCoreRowModel,
  ColumnOrderState,
} from '@tanstack/react-table';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { Button } from 'ncoded-component-library';
import useQueryParams from 'hooks/useQueryParams';
import { ReorderResizeTableProps } from './types';
import SortIcon from 'icons/Sort.icon';
import DraggableCard from 'components/DraggableCard';
import ResolvePagination from 'components/ResolvePagination';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { generateTanstackCols, hasSettings, isSortableCol } from './utils';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import TableRowPlaceholder from './components/TableRowPlaceholder';
import { getIsSortedDescBy, getIsSortApplied, getSortValue } from 'utils';
import TableEmptyState from './components/TableEmptyState';
import useMatchMedia from 'hooks/useMatchMedia';
import SettingsIcon from 'icons/Settings.icon';
import useHorizontalScrollFade from 'hooks/useHorizontalScrollFade';

import './ReorderResizeTable.styles.scss';

const DEFAULT_COL = {
  size: 200, //starting column size
  minSize: 50, //enforced during column resizing
  maxSize: 1000, //enforced during column resizing
};

function ReorderResizeTable<T extends Record<string, any>>(
  props: ReorderResizeTableProps<T>,
) {
  const {
    params: { sort: paramsSort },
    setQueryParam,
  } = useQueryParams<{ sort: string; page: string }>();

  const {
    className,
    data,
    columns,
    headingTranslations,
    tableContentTransformations,
    sortingValues,
    settingsValues,
    deafaultTableColSize,
    customSizes,
    tableHeaderContentTransformations,
    isDragDisabled,
    isResizeDisabled,
    emptyMessage,
    totalPages = 1,
    numberOfPlaceholderRows = 10,
    showPlacehodler = true,
    isDataLoading = false,
    hasHorizontalScrollFade = false,
    columnResizeMode = 'onChange',
    columnResizeDirection = 'ltr',
    sort = paramsSort,
    onReorder,
    getIsRowExpanded,
    getIsRowSelected,
    getRowBackgroundColor,
    getColSpan,
    renderExpandedContent,
    onRowClick,
    onRowDoubleClick,
    updateSort = (value: string) => setQueryParam('sort', value),
  } = props;

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames(
    'reorder-resize-table',
    { loading: isDataLoading },
    className,
  );

  const reorderCB: OnChangeFn<ColumnOrderState> = useCallback(
    (v) => onReorder?.(v as string[]),
    [onReorder],
  );

  const table = useReactTable({
    data,
    columns: generateTanstackCols(
      columns,
      headingTranslations
        ? Object.entries(headingTranslations).reduce(
            (acc, [key, val]) => ({ ...acc, [key]: t(val) }),
            {} as Record<keyof T, string>,
          )
        : columns.reduce(
            (acc, col) => ({ ...acc, [col]: t(col as string) }),
            {} as Record<keyof T, string>,
          ),
      tableHeaderContentTransformations,
      tableContentTransformations,
      customSizes,
    ) as unknown as ColumnDef<T>[],
    columnResizeMode,
    columnResizeDirection,
    getCoreRowModel: getCoreRowModel(),
    getIsRowExpanded,
    initialState: {
      columnOrder: columns as string[],
    },
    state: {
      columnOrder: columns as string[],
    },
    onColumnOrderChange: reorderCB,
    defaultColumn: deafaultTableColSize || DEFAULT_COL,
  });

  const [currentlyDragged, setCurrentlyDragged] = useState('');

  const numberOfColumns = table.getAllColumns().length;

  const numberOfRows = table.getRowCount();

  const { showFade: showHeaderFade, scrollContainerRef } =
    useHorizontalScrollFade(hasHorizontalScrollFade, [
      data,
      columns,
      isDataLoading,
      numberOfRows,
    ]);

  const hasHeaderFade = hasHorizontalScrollFade && showHeaderFade;

  const isEmptyTable = useMemo(() => !numberOfRows, [numberOfRows]);

  const shouldShowPlaceholder = isDataLoading && showPlacehodler;

  const onDragEnd = useCallback(
    async (e: DragEndEvent) => {
      const { active, over } = e;

      setCurrentlyDragged('');
      if (active.id === over.id) return;

      const oldIndex = columns.findIndex((old) => old === active.id);
      const newIndex = columns.findIndex((old) => old === over.id);

      await onReorder?.(arrayMove(columns, oldIndex, newIndex) as any);
    },
    [columns, onReorder],
  );

  if (isPhablet && isEmptyTable)
    return <TableEmptyState emptyMessage={emptyMessage} />;

  return (
    <div
      className={classes}
      style={{ direction: table.options.columnResizeDirection }}
    >
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        onDragStart={(e) => {
          setCurrentlyDragged(e.active.id as string);
        }}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div
          className={classNames('table-scroll-shell', {
            'header-fade': hasHeaderFade,
          })}
        >
          <div ref={scrollContainerRef} className="overflow-x-auto">
            <table
              {...(!isEmptyTable && {
                style: {
                  width: table.getCenterTotalSize(),
                },
              })}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className={classNames({
                      'something-being-dragged': !!currentlyDragged,
                    })}
                  >
                    <SortableContext
                      items={columns as string[]}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => {
                        const currSort = sortingValues?.[header.id];
                        const isSelected = getIsSortApplied(
                          sort,
                          sortingValues?.[header.id],
                        );

                        const isDesc = getIsSortedDescBy(sort, currSort);

                        return (
                          <th
                            key={header.id}
                            {...{
                              colSpan: header.colSpan,
                              style: {
                                width: header.getSize(),
                              },
                            }}
                          >
                            <div
                              className={classNames(header.id, {
                                selected: isSelected,
                              })}
                            >
                              <DraggableCard
                                key={header.id}
                                id={header.id}
                                disabled={isDragDisabled || isEmptyTable}
                              >
                                <div
                                  className={classNames('floating-card', {
                                    grabbable: !isDragDisabled && !isEmptyTable,
                                    'currently-dragged':
                                      currentlyDragged === header.id,
                                  })}
                                >
                                  {!header.isPlaceholder &&
                                    flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                                </div>
                              </DraggableCard>

                              {!currentlyDragged &&
                                isSortableCol(header.id, sortingValues) && (
                                  <Button
                                    disabled={isEmptyTable}
                                    onClick={() => {
                                      const sortingValue =
                                        sortingValues[header.id];

                                      updateSort(
                                        getSortValue(sortingValue, !isDesc),
                                      );
                                    }}
                                    className={classNames('sorting-button', {
                                      double: !isSelected,
                                      selected: isSelected,
                                      rotated: !isDesc,
                                    })}
                                  >
                                    {isSelected ? (
                                      <ArrowdownIcon />
                                    ) : (
                                      <>
                                        <SortIcon />
                                        <SortIcon />
                                      </>
                                    )}
                                  </Button>
                                )}

                              {!currentlyDragged &&
                                hasSettings(header.id, settingsValues) && (
                                  <Button
                                    disabled={isEmptyTable}
                                    onClick={settingsValues[header.id]}
                                    variant="icon"
                                    className="settings-button"
                                    icon={SettingsIcon}
                                  />
                                )}

                              {!isResizeDisabled && !isEmptyTable && (
                                <div
                                  {...{
                                    onDoubleClick: () =>
                                      header.column.resetSize(),
                                    onMouseDown: header.getResizeHandler(),
                                    onTouchStart: header.getResizeHandler(),
                                    className: `resizer ${
                                      table.options.columnResizeDirection
                                    } ${
                                      header.column.getIsResizing()
                                        ? 'isResizing'
                                        : ''
                                    }`,
                                    style: {
                                      transform:
                                        columnResizeMode === 'onEnd' &&
                                        header.column.getIsResizing()
                                          ? `translateX(${
                                              (table.options
                                                .columnResizeDirection === 'rtl'
                                                ? -1
                                                : 1) *
                                              (table.getState().columnSizingInfo
                                                .deltaOffset ?? 0)
                                            }px)`
                                          : '',
                                    },
                                  }}
                                />
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody>
                {shouldShowPlaceholder ? (
                  Array(numberOfPlaceholderRows)
                    .fill(0)
                    .map((_, i) => (
                      <TableRowPlaceholder
                        key={i}
                        numberOfColumns={numberOfColumns}
                      />
                    ))
                ) : isEmptyTable ? (
                  <tr>
                    <td colSpan={numberOfColumns}>
                      <TableEmptyState emptyMessage={emptyMessage} />
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const original = row.original;

                    const hasAdditionalContent = row.getIsExpanded();
                    const cols = row.getVisibleCells();

                    const backgroundColor = getRowBackgroundColor?.(original);
                    const selected = getIsRowSelected?.(original);

                    let colSpanToSkip = 0;

                    return (
                      <Fragment key={row.id}>
                        <tr
                          className={classNames(backgroundColor, {
                            'expandable-row': hasAdditionalContent,
                            selected,
                            clickable: !!onRowClick,
                          })}
                          onClick={() => onRowClick?.(original)}
                          onDoubleClick={() => onRowDoubleClick?.(original)}
                        >
                          {cols.map((cell) => {
                            const colId = cell.column.id;
                            const colSpan = getColSpan?.(original, colId) || 1;
                            const sortAlreadyApplied = sort?.includes(
                              sortingValues?.[colId],
                            );

                            if (colSpanToSkip > 0) {
                              colSpanToSkip--;
                              return null;
                            }

                            colSpanToSkip += colSpan - 1;

                            return (
                              <td
                                key={cell.id}
                                colSpan={colSpan}
                                className={classNames(colId, {
                                  selected: sortAlreadyApplied,
                                })}
                                {...{
                                  style: {
                                    width: cell.column.getSize(),
                                  },
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, {
                                  ...cell.getContext(),
                                  lead: original,
                                })}
                              </td>
                            );
                          })}
                        </tr>

                        {hasAdditionalContent && (
                          <tr className="expanded-row">
                            <td colSpan={cols.length}>
                              {renderExpandedContent(original)}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>
      <ResolvePagination totalPages={totalPages} />
    </div>
  );
}

export default ReorderResizeTable;
